import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import odinLogo from '../assets/odin-logo_website.png';
import './Home.css';
import hero1 from '../assets/hero/iStock-1133785444.jpg';
import hero2 from '../assets/hero/iStock-1342144407.jpg';
import hero3 from '../assets/hero/iStock-2215387590.jpg';
import hero4 from '../assets/hero/iStock-582256640.jpg';
import hero5 from '../assets/hero/iStock-922601466.jpg';

const heroSlides = [hero1, hero2, hero3, hero4, hero5];

const capabilities = [
  {
    number: '01',
    title: 'Equipment integrity records',
    description: 'Keep design information, inspection history, documents, costs, access notes, and corrective actions together for every asset.',
  },
  {
    number: '02',
    title: 'Valve lifecycle tracking',
    description: 'Track pressure relief devices, set pressures, test and repair dates, capacity data, photos, and inspection documentation.',
  },
  {
    number: '03',
    title: 'Boiler inspection matrices',
    description: 'Manage inspection requirements by boiler, connect report evidence to individual matrix lines, and preserve historical reports.',
  },
  {
    number: '04',
    title: 'Planning and accountability',
    description: 'Schedule inspections, monitor compliance, assign action items, and maintain an auditable record of completed work.',
  },
];

const customerBenefits = [
  {
    title: 'Know what needs attention',
    description: 'See overdue inspections, missing dates, compliance gaps, and open action items without rebuilding the answer from spreadsheets.',
    outcome: 'Clearer priorities',
  },
  {
    title: 'Prepare faster for outages',
    description: 'Give planners and inspectors one place to review asset history, inspection requirements, access notes, and supporting documents.',
    outcome: 'Less preparation time',
  },
  {
    title: 'Protect institutional knowledge',
    description: 'Preserve reports, decisions, evidence, and inspection history even when employees, contractors, or service providers change.',
    outcome: 'Continuity across teams',
  },
  {
    title: 'Respond with confidence',
    description: 'Retrieve the record behind a compliance status and export organized data for management reviews, audits, and contractor coordination.',
    outcome: 'Evidence-ready reporting',
  },
];

const workflow = [
  ['Centralize', 'Bring equipment data, reports, drawings, photos, and inspection records into one governed workspace.'],
  ['Connect', 'Relate each inspection, valve, document, and evidence section to the asset and requirement it supports.'],
  ['Act', 'Use current dates, compliance views, exports, and action items to focus work and demonstrate progress.'],
];

const faqs = [
  {
    question: 'What equipment can ODIN manage?',
    answer: 'ODIN supports industrial equipment records, pressure relief valves, and multi-boiler locations with configurable inspection matrices.',
  },
  {
    question: 'Can we export our information?',
    answer: 'Yes. ODIN provides export-ready equipment, action-item, valve, and boiler matrix data for reviews, planning, and offline reporting.',
  },
  {
    question: 'Can contractors or field personnel view an asset?',
    answer: 'Public, read-only equipment and valve profiles can provide controlled access to asset details and supporting documents without allowing changes.',
  },
  {
    question: 'How is access controlled?',
    answer: 'Role-based permissions and company/location assignments determine what authenticated users can view and manage.',
  },
];

export default function Home() {
  const navigate = useNavigate();
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setSlide((current) => (current + 1) % heroSlides.length), 6500);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="home-page">
      <nav className="home-navbar" aria-label="Primary navigation">
        <button type="button" className="home-brand" onClick={() => navigate('/')} aria-label="ODIN Asset Manager home">
          <img src={odinLogo} alt="ODIN Asset Manager" className="odin-navbar-logo" />
        </button>

        <div className="home-nav-links">
          <a href="#benefits">Benefits</a>
          <a href="#platform">Platform</a>
          <a href="#boiler-matrix">Boiler Matrix</a>
          <a href="#workflow">How It Works</a>
        </div>

        <div className="navbar-actions">
          <a className="navbar-demo-link" href="mailto:support@odinassetmanager.com?subject=Request%20an%20ODIN%20Demo">Request a Demo</a>
          <button type="button" className="navbar-login-btn" onClick={() => navigate('/login')}>Client Login</button>
        </div>
      </nav>

      <header className="home-hero-section">
        <div className="hero-slideshow" aria-hidden="true">
          {heroSlides.map((src, index) => (
            <img key={src} src={src} alt="" className={`slide ${index === slide ? 'active' : ''}`} />
          ))}
          <div className="slide-overlay" />
        </div>

        <div className="hero-content">
          <span className="home-eyebrow">INDUSTRIAL ASSET INTEGRITY</span>
          <h1>Know what’s due. Prove what’s done. Keep every asset inspection-ready.</h1>
          <p>
            ODIN gives reliability and maintenance teams a clear, connected view of equipment,
            pressure relief valves, boilers, inspection evidence, and the work required next—so
            they spend less time searching for information and more time improving asset integrity.
          </p>
          <div className="cta-buttons">
            <a href="mailto:support@odinassetmanager.com?subject=Request%20an%20ODIN%20Demo" className="cta-button primary">Schedule a Demonstration</a>
            <a href="#platform" className="cta-button secondary">Explore the Platform</a>
          </div>
          <div className="hero-scope" aria-label="ODIN capabilities">
            <span>Equipment</span><i />
            <span>Valves</span><i />
            <span>Boilers</span><i />
            <span>Inspection Evidence</span>
          </div>
        </div>

        <aside className="hero-system-card" aria-label="ODIN system overview">
          <div className="system-card-heading">
            <span>ASSET OVERVIEW</span>
            <strong>Inspection intelligence</strong>
          </div>
          <div className="system-status-row"><span className="status-dot current" /> <div><strong>Current requirements</strong><small>Dates and inspection intervals</small></div></div>
          <div className="system-status-row"><span className="status-dot evidence" /> <div><strong>Connected evidence</strong><small>Reports, pages, and documents</small></div></div>
          <div className="system-status-row"><span className="status-dot action" /> <div><strong>Visible priorities</strong><small>Compliance and action items</small></div></div>
          <div className="system-card-footer"><span>Secure cloud workspace</span><span>Export ready</span></div>
        </aside>
      </header>

      <section className="home-proof-strip" aria-label="Platform highlights">
        <div><strong>Reduce administrative work</strong><span>Stop rebuilding records across files</span></div>
        <div><strong>Make priorities visible</strong><span>Know what is overdue or incomplete</span></div>
        <div><strong>Preserve inspection history</strong><span>Keep evidence tied to the requirement</span></div>
        <div><strong>Support better decisions</strong><span>Use current, exportable information</span></div>
      </section>

      <section className="industry-band" aria-label="Industries served">
        <span>BUILT FOR INDUSTRIAL FACILITIES</span>
        <div><strong>Pulp &amp; Paper</strong><strong>Chemical</strong><strong>Oil &amp; Gas</strong><strong>Manufacturing</strong></div>
      </section>

      <section className="home-section benefits-section" id="benefits">
        <div className="section-heading">
          <span className="home-eyebrow">THE VALUE OF ODIN</span>
          <h2>Turn scattered inspection data into an operating advantage.</h2>
          <p>
            ODIN helps facilities replace fragmented records and manual follow-up with a dependable
            system for understanding risk, preparing work, and demonstrating what has been completed.
          </p>
        </div>
        <div className="benefit-grid">
          {customerBenefits.map((benefit) => (
            <article className="benefit-card" key={benefit.title}>
              <span className="benefit-outcome">{benefit.outcome}</span>
              <h3>{benefit.title}</h3>
              <p>{benefit.description}</p>
            </article>
          ))}
        </div>
        <div className="value-summary">
          <span>THE RESULT</span>
          <p>
            A more organized integrity program, fewer avoidable information gaps, stronger coordination
            between facility teams and contractors, and a clearer path from inspection findings to action.
          </p>
        </div>
      </section>

      <section className="home-section platform-section" id="platform">
        <div className="section-heading">
          <span className="home-eyebrow">THE ODIN PLATFORM</span>
          <h2>Manage the fixed-equipment program—not just one asset class.</h2>
          <p>Each capability is designed to reduce manual recordkeeping while making inspection and compliance information easier to use.</p>
        </div>
        <div className="capability-grid">
          {capabilities.map((item) => (
            <article className="capability-card" key={item.number}>
              <span className="capability-number">{item.number}</span>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="home-section boiler-section" id="boiler-matrix">
        <div className="boiler-copy">
          <span className="home-eyebrow">BOILER MATRIX + REPORT EVIDENCE</span>
          <h2>Turn a boiler inspection report into evidence your team can use.</h2>
          <p>
            Maintain a configurable inspection matrix for every boiler, select the supporting PDF pages,
            and link that evidence directly to the applicable inspection line without duplicating report sections.
          </p>
          <p className="section-value-note">
            Your team can verify coverage quickly, carry forward valid historical evidence when an item is not
            inspected in the newest report, and deliver a complete matrix without manually assembling another workbook.
          </p>
          <ul className="feature-check-list">
            <li>Track multiple boilers and historical inspection reports by location.</li>
            <li>Reuse evidence across matrix lines while preserving its original report source.</li>
            <li>Record inspection methods, intervals, last dates, N/A status, and team notes.</li>
            <li>Export the complete boiler matrix to Excel for offline review and sharing.</li>
          </ul>
        </div>

        <div className="matrix-preview" aria-label="Example exportable boiler matrix">
          <div className="matrix-preview-toolbar">
            <div><span>RECOVERY BOILER</span><strong>Inspection Matrix</strong></div>
            <span className="export-pill">EXPORT XLSX</span>
          </div>
          <div className="matrix-preview-header"><span>Inspection line</span><span>Method</span><span>Evidence</span></div>
          {[
            ['Furnace floor tubes', 'VT', 'Linked'],
            ['Wall tube thickness', 'UT', 'Linked'],
            ['Smelt spout openings', 'PT / VT', 'Review'],
            ['Superheater elements', 'MLO', 'Linked'],
          ].map(([line, method, status]) => (
            <div className="matrix-preview-row" key={line}>
              <span>{line}</span><span className="method-pill">{method}</span><span className={`evidence-state ${status.toLowerCase()}`}>{status}</span>
            </div>
          ))}
          <div className="matrix-preview-note">PDF page evidence · inspection history · notes · export</div>
        </div>
      </section>

      <section className="home-section valve-section">
        <div className="valve-visual" aria-hidden="true">
          <div className="valve-ring ring-one" /><div className="valve-ring ring-two" />
          <div className="valve-record-card">
            <span>PRESSURE RELIEF DEVICE</span>
            <strong>PRV-204</strong>
            <dl><div><dt>Set pressure</dt><dd>Tracked</dd></div><div><dt>Test history</dt><dd>Current</dd></div><div><dt>Documents</dt><dd>Connected</dd></div></dl>
          </div>
        </div>
        <div className="valve-copy">
          <span className="home-eyebrow">VALVE MANAGEMENT</span>
          <h2>Keep every pressure relief device connected to its equipment and history.</h2>
          <p>ODIN tracks valve identification, service data, test and repair schedules, capacity information, photos, and inspection documents in one accessible profile.</p>
          <p className="section-value-note">That means less time reconciling valve lists, fewer disconnected records, and faster access to the information needed before testing or service work.</p>
          <p className="public-profile-note"><strong>Field-ready access:</strong> authorized public profile links can provide read-only valve and equipment information without exposing editing controls.</p>
        </div>
      </section>

      <section className="home-section workflow-section" id="workflow">
        <div className="section-heading compact">
          <span className="home-eyebrow">FROM DATA TO DECISIONS</span>
          <h2>A practical integrity workflow.</h2>
        </div>
        <div className="workflow-grid">
          {workflow.map(([title, description], index) => (
            <article key={title}><span>0{index + 1}</span><h3>{title}</h3><p>{description}</p></article>
          ))}
        </div>
      </section>

      <section className="home-section faq-section">
        <div className="section-heading compact">
          <span className="home-eyebrow">COMMON QUESTIONS</span>
          <h2>Designed for real operating environments.</h2>
        </div>
        <div className="faq-grid">
          {faqs.map((faq) => <article className="faq-card" key={faq.question}><h3>{faq.question}</h3><p>{faq.answer}</p></article>)}
        </div>
      </section>

      <section className="home-final-cta">
        <div><span className="home-eyebrow">SEE ODIN WITH YOUR WORKFLOW</span><h2>Bring clarity to your asset integrity program.</h2><p>Schedule a focused demonstration for your facility, equipment, and inspection process.</p></div>
        <a href="mailto:support@odinassetmanager.com?subject=Request%20an%20ODIN%20Demo">Request a Demonstration</a>
      </section>

      <footer className="home-footer">
        <div className="footer-brand"><img src={odinLogo} alt="" /><span><strong>ODIN Asset Manager</strong><small>Operations Data Information Network</small></span></div>
        <div className="footer-contact"><a href="mailto:support@odinassetmanager.com">support@odinassetmanager.com</a><span>(337) 250-5171</span></div>
        <div className="footer-copy">© {new Date().getFullYear()} ODIN Asset Manager</div>
      </footer>
    </div>
  );
}
